import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {get, push, ref, serverTimestamp, set} from 'firebase/database';
import {database} from '../../services/firebase';
import {useAuth} from '../../contexts/AuthContext';

// Options de difficulté
const difficultyOptions = [
    {value: 'easy', label: 'Facile'},
    {value: 'medium', label: 'Moyen'},
    {value: 'hard', label: 'Difficile'}
];

// Catégories de quiz
const categoryOptions = [
    {value: 'old-testament', label: 'Ancien Testament'},
    {value: 'new-testament', label: 'Nouveau Testament'},
    {value: 'prophets', label: 'Prophètes'},
    {value: 'gospels', label: 'Évangiles'},
    {value: 'paul-letters', label: 'Épîtres de Paul'},
    {value: 'history', label: 'Histoire biblique'},
    {value: 'characters', label: 'Personnages bibliques'},
    {value: 'psalms', label: 'Psaumes'},
    {value: 'proverbs', label: 'Proverbes'},
    {value: 'parables', label: 'Paraboles'},
    {value: 'general', label: 'Connaissances générales'}
];

export default function QuizForm() {
    const {quizId} = useParams();
    const navigate = useNavigate();
    const {currentUser, userRole} = useAuth();
    const isEditMode = !!quizId;

    // États pour le formulaire
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // États pour les métadonnées du quiz
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('general');
    const [difficulty, setDifficulty] = useState('medium');
    const [timePerQuestion, setTimePerQuestion] = useState(30);
    const [active, setActive] = useState(true);
    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState('');

    // États pour les questions
    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(null);
    const [allQuestions, setAllQuestions] = useState([]);
    const [filteredQuestions, setFilteredQuestions] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [questionFilter, setQuestionFilter] = useState({
        category: '',
        difficulty: ''
    });

    // Récupérer les données du quiz en mode édition
    useEffect(() => {
        async function fetchQuizData() {
            if (userRole !== 'admin') {
                setError('Accès non autorisé. Vous devez être administrateur pour créer ou éditer un quiz.');
                setLoading(false);
                return;
            }

            if (isEditMode) {
                try {
                    const quizRef = ref(database, `quizzes/${quizId}`);
                    const quizSnapshot = await get(quizRef);

                    if (!quizSnapshot.exists()) {
                        setError('Quiz introuvable.');
                        setLoading(false);
                        return;
                    }

                    const quizData = quizSnapshot.val();

                    // Remplir les métadonnées
                    setTitle(quizData.metadata.title || '');
                    setDescription(quizData.metadata.description || '');
                    setCategory(quizData.metadata.category || 'general');
                    setDifficulty(quizData.metadata.difficulty || 'medium');
                    setTimePerQuestion(quizData.metadata.timePerQuestion || 30);
                    setActive(quizData.metadata.active ?? true);
                    setTags(quizData.metadata.tags || []);

                    // Remplir les questions
                    if (quizData.questions) {
                        setQuestions(quizData.questions.map((q, index) => ({...q, id: `existing-${index}`})));
                    }
                } catch (error) {
                    console.error("Erreur lors de la récupération du quiz:", error);
                    setError("Une erreur s'est produite lors de la récupération du quiz.");
                }
            }

            // Charger toutes les questions disponibles de la banque de questions
            try {
                const questionBankRef = ref(database, 'questionBank');
                const questionBankSnapshot = await get(questionBankRef);

                if (questionBankSnapshot.exists()) {
                    const questionsData = questionBankSnapshot.val();
                    const questionsList = Object.entries(questionsData).map(([id, data]) => ({
                        id,
                        ...data
                    }));

                    setAllQuestions(questionsList);
                    setFilteredQuestions(questionsList);
                }
            } catch (error) {
                console.error("Erreur lors de la récupération des questions:", error);
            }

            setLoading(false);
        }

        fetchQuizData();
    }, [quizId, isEditMode, userRole]);

    // Filtrer les questions selon les critères
    useEffect(() => {
        let filtered = [...allQuestions];

        // Appliquer le filtre de recherche
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(q =>
                q.text.toLowerCase().includes(term) ||
                q.options.some(opt => opt.toLowerCase().includes(term))
            );
        }

        // Appliquer le filtre de catégorie
        if (questionFilter.category) {
            filtered = filtered.filter(q => q.category === questionFilter.category);
        }

        // Appliquer le filtre de difficulté
        if (questionFilter.difficulty) {
            filtered = filtered.filter(q => q.difficulty === questionFilter.difficulty);
        }

        setFilteredQuestions(filtered);
    }, [allQuestions, searchTerm, questionFilter]);

    // Ajouter un tag
    const handleAddTag = () => {
        if (tagInput.trim() && !tags.includes(tagInput.trim())) {
            setTags([...tags, tagInput.trim()]);
            setTagInput('');
        }
    };

    // Supprimer un tag
    const handleRemoveTag = (tagToRemove) => {
        setTags(tags.filter(tag => tag !== tagToRemove));
    };

    // Ajouter une question de la banque au quiz
    const handleAddQuestionFromBank = (question) => {
        // Vérifier si la question est déjà dans le quiz
        const isQuestionAlreadyAdded = questions.some(q =>
            q.id === question.id || q.text === question.text
        );

        if (!isQuestionAlreadyAdded) {
            setQuestions([...questions, {...question, points: 1}]);
            setSuccess(`Question "${question.text.substring(0, 30)}..." ajoutée au quiz.`);

            // Effacer le message de succès après 3 secondes
            setTimeout(() => setSuccess(''), 3000);
        } else {
            setError('Cette question est déjà dans le quiz.');

            // Effacer le message d'erreur après 3 secondes
            setTimeout(() => setError(''), 3000);
        }
    };

    // Créer une nouvelle question
    const handleCreateNewQuestion = () => {
        const newQuestion = {
            id: `new-${Date.now()}`,
            text: '',
            options: ['', '', '', ''],
            correctAnswer: 0,
            verse: '',
            explanation: '',
            category: category,
            difficulty: difficulty,
            points: 1
        };

        setQuestions([...questions, newQuestion]);
        setCurrentQuestionIndex(questions.length);
    };

    // Modifier une question
    const handleEditQuestion = (index) => {
        setCurrentQuestionIndex(index);
    };

    // Supprimer une question
    const handleRemoveQuestion = (index) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cette question ?')) {
            const updatedQuestions = [...questions];
            updatedQuestions.splice(index, 1);
            setQuestions(updatedQuestions);

            if (currentQuestionIndex === index) {
                setCurrentQuestionIndex(null);
            } else if (currentQuestionIndex > index) {
                setCurrentQuestionIndex(currentQuestionIndex - 1);
            }
        }
    };

    // Réorganiser les questions (monter)
    const handleMoveQuestionUp = (index) => {
        if (index > 0) {
            const updatedQuestions = [...questions];
            const temp = updatedQuestions[index];
            updatedQuestions[index] = updatedQuestions[index - 1];
            updatedQuestions[index - 1] = temp;
            setQuestions(updatedQuestions);

            if (currentQuestionIndex === index) {
                setCurrentQuestionIndex(index - 1);
            } else if (currentQuestionIndex === index - 1) {
                setCurrentQuestionIndex(index);
            }
        }
    };

    // Réorganiser les questions (descendre)
    const handleMoveQuestionDown = (index) => {
        if (index < questions.length - 1) {
            const updatedQuestions = [...questions];
            const temp = updatedQuestions[index];
            updatedQuestions[index] = updatedQuestions[index + 1];
            updatedQuestions[index + 1] = temp;
            setQuestions(updatedQuestions);

            if (currentQuestionIndex === index) {
                setCurrentQuestionIndex(index + 1);
            } else if (currentQuestionIndex === index + 1) {
                setCurrentQuestionIndex(index);
            }
        }
    };

    // Mettre à jour une question
    const handleUpdateQuestion = (updatedQuestion) => {
        if (currentQuestionIndex !== null) {
            const updatedQuestions = [...questions];
            updatedQuestions[currentQuestionIndex] = updatedQuestion;
            setQuestions(updatedQuestions);
        }
    };

    // Sauvegarder le quiz
    const handleSaveQuiz = async () => {
        if (!title.trim()) {
            setError('Le titre du quiz est requis.');
            return;
        }

        if (questions.length === 0) {
            setError('Le quiz doit contenir au moins une question.');
            return;
        }

        // Vérifier que toutes les questions sont complètes
        const incompleteQuestions = questions.filter(q =>
            !q.text.trim() ||
            q.options.some(opt => !opt.trim()) ||
            !q.verse.trim()
        );

        if (incompleteQuestions.length > 0) {
            setError(`${incompleteQuestions.length} question(s) incomplète(s). Veuillez remplir tous les champs.`);
            return;
        }

        try {
            setLoading(true);

            const quizData = {
                metadata: {
                    title,
                    description,
                    category,
                    difficulty,
                    timePerQuestion: Number(timePerQuestion),
                    active,
                    tags,
                    totalQuestions: questions.length,
                    createdBy: currentUser.uid,
                    updatedAt: serverTimestamp()
                },
                questions: questions.map(q => ({
                    text: q.text,
                    options: q.options,
                    correctAnswer: q.correctAnswer,
                    verse: q.verse,
                    explanation: q.explanation,
                    category: q.category,
                    difficulty: q.difficulty,
                    points: Number(q.points) || 1
                }))
            };

            if (isEditMode) {
                // Mode édition
                const quizRef = ref(database, `quizzes/${quizId}`);
                await set(quizRef, {
                    ...quizData,
                    metadata: {
                        ...quizData.metadata,
                        createdAt: serverTimestamp() // Conserver la date de création originale
                    }
                });
                setSuccess('Quiz mis à jour avec succès !');
            } else {
                // Mode création
                const quizzesRef = ref(database, 'quizzes');
                const newQuizRef = push(quizzesRef);
                await set(newQuizRef, {
                    ...quizData,
                    metadata: {
                        ...quizData.metadata,
                        createdAt: serverTimestamp()
                    }
                });
                setSuccess('Quiz créé avec succès !');

                // Rediriger vers la page d'édition du nouveau quiz
                setTimeout(() => {
                    navigate(`/admin/quiz/${newQuizRef.key}`);
                }, 1500);
            }

            // Ajouter les nouvelles questions à la banque de questions
            for (const question of questions) {
                // Si c'est une nouvelle question (pas déjà dans la banque)
                if (question.id.startsWith('new-')) {
                    const questionBankRef = ref(database, 'questionBank');
                    const newQuestionRef = push(questionBankRef);
                    await set(newQuestionRef, {
                        text: question.text,
                        options: question.options,
                        correctAnswer: question.correctAnswer,
                        verse: question.verse,
                        explanation: question.explanation,
                        category: question.category,
                        difficulty: question.difficulty,
                        sourceQuizId: isEditMode ? quizId : newQuestionRef.key,
                        createdBy: currentUser.uid,
                        createdAt: serverTimestamp()
                    });
                }
            }

            setLoading(false);
        } catch (error) {
            console.error("Erreur lors de la sauvegarde du quiz:", error);
            setError("Une erreur s'est produite lors de la sauvegarde du quiz.");
            setLoading(false);
        }
    };

    // Composant d'édition de question
    const QuestionEditor = ({question}) => {
        const [localQuestion, setLocalQuestion] = useState({...question});

        // Mettre à jour le champ de la question
        const handleChangeField = (field, value) => {
            setLocalQuestion({...localQuestion, [field]: value});
            handleUpdateQuestion({...localQuestion, [field]: value});
        };

        // Mettre à jour une option
        const handleChangeOption = (index, value) => {
            const updatedOptions = [...localQuestion.options];
            updatedOptions[index] = value;
            setLocalQuestion({...localQuestion, options: updatedOptions});
            handleUpdateQuestion({...localQuestion, options: updatedOptions});
        };

        return (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6 bg-indigo-50">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Édition de la question
                    </h3>
                </div>
                <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                        <div className="sm:col-span-6">
                            <label htmlFor="question-text" className="block text-sm font-medium text-gray-700">
                                Question
                            </label>
                            <div className="mt-1">
                <textarea
                    id="question-text"
                    name="question-text"
                    rows={3}
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    value={localQuestion.text}
                    onChange={(e) => handleChangeField('text', e.target.value)}
                />
                            </div>
                        </div>

                        <div className="sm:col-span-3">
                            <label htmlFor="question-category" className="block text-sm font-medium text-gray-700">
                                Catégorie
                            </label>
                            <div className="mt-1">
                                <select
                                    id="question-category"
                                    name="question-category"
                                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                    value={localQuestion.category}
                                    onChange={(e) => handleChangeField('category', e.target.value)}
                                >
                                    {categoryOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="sm:col-span-3">
                            <label htmlFor="question-difficulty" className="block text-sm font-medium text-gray-700">
                                Difficulté
                            </label>
                            <div className="mt-1">
                                <select
                                    id="question-difficulty"
                                    name="question-difficulty"
                                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                    value={localQuestion.difficulty}
                                    onChange={(e) => handleChangeField('difficulty', e.target.value)}
                                >
                                    {difficultyOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="sm:col-span-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Options
                            </label>
                            <div className="space-y-3">
                                {localQuestion.options.map((option, index) => (
                                    <div key={index} className="flex items-center">
                                        <input
                                            type="radio"
                                            id={`option-${index}`}
                                            name="correct-answer"
                                            className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                                            checked={localQuestion.correctAnswer === index}
                                            onChange={() => handleChangeField('correctAnswer', index)}
                                        />
                                        <label htmlFor={`option-${index}`}
                                               className="ml-2 block text-sm text-gray-700 w-8">
                                            {['A', 'B', 'C', 'D'][index]}
                                        </label>
                                        <input
                                            type="text"
                                            className="ml-2 flex-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            placeholder={`Option ${['A', 'B', 'C', 'D'][index]}`}
                                            value={option}
                                            onChange={(e) => handleChangeOption(index, e.target.value)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="sm:col-span-6">
                            <label htmlFor="question-verse" className="block text-sm font-medium text-gray-700">
                                Référence biblique
                            </label>
                            <div className="mt-1">
                                <input
                                    type="text"
                                    id="question-verse"
                                    name="question-verse"
                                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                    placeholder="Ex: Jean 3:16"
                                    value={localQuestion.verse}
                                    onChange={(e) => handleChangeField('verse', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="sm:col-span-6">
                            <label htmlFor="question-explanation" className="block text-sm font-medium text-gray-700">
                                Explication (affichée après la réponse)
                            </label>
                            <div className="mt-1">
                <textarea
                    id="question-explanation"
                    name="question-explanation"
                    rows={3}
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    value={localQuestion.explanation}
                    onChange={(e) => handleChangeField('explanation', e.target.value)}
                />
                            </div>
                        </div>

                        <div className="sm:col-span-2">
                            <label htmlFor="question-points" className="block text-sm font-medium text-gray-700">
                                Points
                            </label>
                            <div className="mt-1">
                                <input
                                    type="number"
                                    id="question-points"
                                    name="question-points"
                                    min="1"
                                    max="10"
                                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                    value={localQuestion.points}
                                    onChange={(e) => handleChangeField('points', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo-100">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            <header className="bg-white shadow">
                <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
                    <h1 className="text-3xl font-bold text-gray-900">
                        {isEditMode ? 'Modifier le Quiz' : 'Créer un Nouveau Quiz'}
                    </h1>
                </div>
            </header>

            <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                {error && (
                    <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">
                                    {error}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {success && (
                    <div className="mb-4 bg-green-50 border-l-4 border-green-400 p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-green-700">
                                    {success}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
                    <div className="px-4 py-5 sm:px-6 bg-indigo-50">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                            Informations générales
                        </h3>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500">
                            Détails et configuration du quiz
                        </p>
                    </div>
                    <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                            <div className="sm:col-span-4">
                                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                                    Titre
                                </label>
                                <div className="mt-1">
                                    <input
                                        type="text"
                                        name="title"
                                        id="title"
                                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="sm:col-span-4">
                                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                    Description
                                </label>
                                <div className="mt-1">
                  <textarea
                      id="description"
                      name="description"
                      rows={3}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                  />
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                                    Catégorie
                                </label>
                                <div className="mt-1">
                                    <select
                                        id="category"
                                        name="category"
                                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value)}
                                    >
                                        {categoryOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label htmlFor="difficulty" className="block text-sm font-medium text-gray-700">
                                    Difficulté
                                </label>
                                <div className="mt-1">
                                    <select
                                        id="difficulty"
                                        name="difficulty"
                                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                        value={difficulty}
                                        onChange={(e) => setDifficulty(e.target.value)}
                                    >
                                        {difficultyOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="sm:col-span-2">
                                <label htmlFor="timePerQuestion" className="block text-sm font-medium text-gray-700">
                                    Temps par question (secondes)
                                </label>
                                <div className="mt-1">
                                    <input
                                        type="number"
                                        name="timePerQuestion"
                                        id="timePerQuestion"
                                        min="10"
                                        max="120"
                                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                        value={timePerQuestion}
                                        onChange={(e) => setTimePerQuestion(Number(e.target.value))}
                                    />
                                </div>
                            </div>

                            <div className="sm:col-span-2">
                                <div className="flex items-center h-full">
                                    <input
                                        id="active"
                                        name="active"
                                        type="checkbox"
                                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                        checked={active}
                                        onChange={(e) => setActive(e.target.checked)}
                                    />
                                    <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
                                        Quiz actif
                                    </label>
                                </div>
                            </div>

                            <div className="sm:col-span-6">
                                <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
                                    Tags
                                </label>
                                <div className="mt-1">
                                    <div className="flex">
                                        <input
                                            type="text"
                                            name="tagInput"
                                            id="tagInput"
                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            value={tagInput}
                                            onChange={(e) => setTagInput(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddTag}
                                            className="ml-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        >
                                            Ajouter
                                        </button>
                                    </div>
                                    <div className="mt-2 flex flex-wrap">
                                        {tags.map((tag) => (
                                            <span
                                                key={tag}
                                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 mr-2 mb-2"
                                            >
                        {tag}
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveTag(tag)}
                                                    className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-indigo-400 hover:text-indigo-700 focus:outline-none"
                                                >
                          <span className="sr-only">Supprimer le tag {tag}</span>
                          <svg className="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                            <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                          </svg>
                        </button>
                      </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <div className="lg:col-span-2">
                        <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
                            <div className="px-4 py-5 sm:px-6 bg-indigo-50 flex justify-between items-center">
                                <div>
                                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                                        Questions du quiz
                                    </h3>
                                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                        {questions.length} question(s) ajoutée(s)
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleCreateNewQuestion}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    Nouvelle question
                                </button>
                            </div>

                            <div className="divide-y divide-gray-200">
                                {questions.length > 0 ? (
                                    questions.map((question, index) => (
                                        <div key={question.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                                            <div className="flex items-center justify-between">
                                                <p className={`text-sm font-medium ${currentQuestionIndex === index ? 'text-indigo-600' : 'text-gray-900'}`}>
                                                    {index + 1}. {question.text || <em className="text-gray-400">Nouvelle question</em>}
                                                </p>
                                                <div className="flex space-x-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleMoveQuestionUp(index)}
                                                        disabled={index === 0}
                                                        className={`inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white ${
                                                            index === 0 ? 'bg-gray-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                                                        } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                                                    >
                                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleMoveQuestionDown(index)}
                                                        disabled={index === questions.length - 1}
                                                        className={`inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white ${
                                                            index === questions.length - 1 ? 'bg-gray-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                                                        } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                                                    >
                                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleEditQuestion(index)}
                                                        className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                    >
                                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveQuestion(index)}
                                                        className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                                    >
                                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="mt-2 flex items-center text-sm text-gray-500">
                                                <p>
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              question.difficulty === 'easy'
                                  ? 'bg-green-100 text-green-800'
                                  : question.difficulty === 'medium'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-red-100 text-red-800'
                          }`}>
                            {question.difficulty === 'easy'
                                ? 'Facile'
                                : question.difficulty === 'medium'
                                    ? 'Moyen'
                                    : 'Difficile'
                            }
                          </span>
                                                    <span className="ml-2">{question.verse || 'Pas de référence'}</span>
                                                    <span className="ml-2">{question.points || 1} point{question.points > 1 ? 's' : ''}</span>
                                                </p>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="px-4 py-5 sm:px-6 text-center">
                                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune question</h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            Commencez par créer une nouvelle question ou ajoutez-en une depuis la banque de questions.
                                        </p>
                                        <div className="mt-6">
                                            <button
                                                type="button"
                                                onClick={handleCreateNewQuestion}
                                                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            >
                                                <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                                                </svg>
                                                Nouvelle question
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {currentQuestionIndex !== null && questions[currentQuestionIndex] && (
                            <div className="mt-6">
                                <QuestionEditor question={questions[currentQuestionIndex]} />
                            </div>
                        )}
                    </div>

                    <div className="lg:col-span-1">
                        <div className="bg-white shadow sm:rounded-lg mb-6">
                            <div className="px-4 py-5 sm:px-6 bg-indigo-50">
                                <h3 className="text-lg leading-6 font-medium text-gray-900">
                                    Banque de questions
                                </h3>
                                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                    Ajouter des questions existantes
                                </p>
                            </div>
                            <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                                <div className="mb-4">
                                    <label htmlFor="search" className="sr-only">Rechercher des questions</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                        </div>
                                        <input
                                            id="search"
                                            name="search"
                                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            placeholder="Rechercher des questions"
                                            type="search"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="flex space-x-2 mb-4">
                                    <select
                                        className="block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        value={questionFilter.category}
                                        onChange={(e) => setQuestionFilter({...questionFilter, category: e.target.value})}
                                    >
                                        <option value="">Toutes catégories</option>
                                        {categoryOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>

                                    <select
                                        className="block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        value={questionFilter.difficulty}
                                        onChange={(e) => setQuestionFilter({...questionFilter, difficulty: e.target.value})}
                                    >
                                        <option value="">Toutes difficultés</option>
                                        {difficultyOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="overflow-y-auto max-h-96">
                                    {filteredQuestions.length > 0 ? (
                                        <ul className="divide-y divide-gray-200">
                                            {filteredQuestions.map((question) => (
                                                <li key={question.id} className="py-3">
                                                    <div className="flex justify-between">
                                                        <p className="text-sm font-medium text-gray-900 truncate">
                                                            {question.text}
                                                        </p>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleAddQuestionFromBank(question)}
                                                            className="ml-2 inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                        >
                                                            Ajouter
                                                        </button>
                                                    </div>
                                                    <div className="mt-1 flex text-xs text-gray-500">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                question.difficulty === 'easy'
                                    ? 'bg-green-100 text-green-800'
                                    : question.difficulty === 'medium'
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : 'bg-red-100 text-red-800'
                            }`}>
                              {question.difficulty === 'easy'
                                  ? 'Facile'
                                  : question.difficulty === 'medium'
                                      ? 'Moyen'
                                      : 'Difficile'
                              }
                            </span>
                                                        <span className="ml-2">{question.verse || 'Pas de référence'}</span>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <div className="text-center py-4">
                                            <p className="text-sm text-gray-500">Aucune question correspondant aux critères</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="bg-white shadow sm:rounded-lg">
                            <div className="px-4 py-5 sm:p-6">
                                <h3 className="text-lg leading-6 font-medium text-gray-900">
                                    Actions
                                </h3>
                                <div className="mt-5 space-y-3">
                                    <button
                                        type="button"
                                        onClick={handleSaveQuiz}
                                        disabled={loading}
                                        className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                                    >
                                        {loading ? 'Enregistrement...' : isEditMode ? 'Enregistrer les modifications' : 'Créer le quiz'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/admin/dashboard')}
                                        className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    >
                                        Annuler
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
